import { defineStore } from 'pinia';
import { useDbStore } from '../dbStore';
import { useERezeptStore } from '../eRezeptStore';
import { useBasicResourcesStore } from './basicResources';

export const useMuster15ResourcesStore = defineStore('muster15Resources', {
  actions: {
  
    createMuster15Bundle() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const basicResourcesStore = useBasicResourcesStore();
      // Erstelle die erforderlichen FHIR-Ressourcen
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      const organizationResource = basicResourcesStore.createOrganizationResource(dbStore.getSelectedOrganization);
      const deviceRequestResource = this.createDeviceRequestMuster15Resource(eRezeptStore.muster15FormData);
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);

      const compositionResource = basicResourcesStore.createCompositionHilfsmittelResource({
        id: crypto.randomUUID(),
        patientId: patientResource.id,
        practitionerId: practitionerResource.id,
        organizationId: organizationResource.id,
        deviceRequestId: deviceRequestResource.id,
        coverageId: coverageResource.id,
        musterType: 'e15H',
        date: new Date().toISOString(),
        legalBasis: '00',
      });

      const resources = [
        compositionResource,
        deviceRequestResource,
        patientResource,
        practitionerResource,
        organizationResource,
        coverageResource,
      ];

      const bundle = basicResourcesStore.createHilfsmittelFHIRBundle(resources);
      return bundle;
    },

    createDeviceRequestMuster15Resource() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const muster15FormData = eRezeptStore.muster15formData;
      const basicResourcesStore = useBasicResourcesStore();
      // Erstelle die erforderlichen FHIR-Ressourcen basierend auf muster15FormData
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);

      const deviceRequestResource = {
        resourceType: 'DeviceRequest',
        meta: {
          profile: [
            'https://fhir.carelogic.de/StructureDefinition/CL_PR_ERP_Prescription_Device|0.2.0'
          ]
        },
        extension: [
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_Folgeversorgung',
            valueBoolean: muster15FormData.folgeversorgung || false
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_BVG',
            valueBoolean: muster15FormData.bvg || false
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_Accident',
            valueExtension: {
              extension: [
                {
                  url: 'Unfallkennzeichen',
                  valueCoding: {
                    system: 'https://fhir.kbv.de/ValueSet/KBV_VS_ERP_Accident_Type',
                    code: muster15FormData.accidentType
                  }
                }
              ]
            }
          },
          {
            url: 'https://fhir.carelogic.de/StructureDefinition/CL_EX_ERP_DiagnosisICD',
            valueCoding: {
              system: 'http://fhir.de/ValueSet/bfarm/icd-10-gm',
              code: muster15FormData.diagnosisICD,
              display: muster15FormData.diagnosisText
            }
          }
        ],
        parameter: [
          // Rechtes Ohr
          ...this.createOhrParameters(muster15FormData.ohren.rechts, 'Rechts'),
          // Linkes Ohr
          ...this.createOhrParameters(muster15FormData.ohren.links, 'Links')
        ],
        subject: {
          reference: `Patient/${patientResource.id}`
        },
        authoredOn: muster15FormData.ausstellungsdatum,
        requester: {
          reference: `Practitioner/${practitionerResource.id}`
        },
        insurance: [
          {
            reference: `Coverage/${coverageResource.id}`
          }
        ],
        note: [
          {
            text: muster15FormData.note
          }
        ]
      };

    
      return deviceRequestResource;
    },

    createOhrParameters(ohrData, side) {
      const parameters = [];
    
      // Hörverlust
      const hoerverlustFrequencies = ['10KHz', '8KHz', '6KHz', '4KHz', '3KHz', '2KHz', '15KHz', '1KHz', '05KHz', '025KHz', '0125KHz'];
      hoerverlustFrequencies.forEach(freq => {
        if (ohrData.hoerverlust[freq]) {
          parameters.push({
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: this.getLoincCodeForHoerverlust(freq, side),
                  display: `Hörverlust LL ${side} - ${freq}`
                }
              ]
            },
            valueQuantity: {
              value: parseInt(ohrData.hoerverlust[freq]),
              unit: 'Db',
              system: 'http://unitsofmeasure.org',
              code: 'Db'
            }
          });
        }
      });
    
      // Verständlichkeit
      ohrData.verstaendlichkeit.forEach((value, index) => {
        if (value !== null) {
          const verstaendlichkeitValues = ['0', '20', '40', '60', '80', '100'];
          const verstaendlichkeit = verstaendlichkeitValues[index];
          parameters.push({
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: `99910-${index + 1}`,
                  display: `Verständlichkeit ${side} - ${verstaendlichkeit}%`
                }
              ]
            },
            valueQuantity: {
              value: value,
              unit: '%',
              system: 'http://unitsofmeasure.org',
              code: '%'
            }
          });
        }
      });
    
      // Stapediusreflex
      ['05KHz', '1KHz', '2KHz', '4KHz'].forEach(freq => {
        ['SL', 'HL'].forEach(reflexType => {
          const value = ohrData.stapediusreflex[reflexType][freq];
          if (value) {
            parameters.push({
              code: {
                coding: [
                  {
                    system: 'http://loinc.org',
                    code: `99911-${freq}-${reflexType}-${side}`,
                    display: `Stapediusreflex ${side} - ${freq} - ${reflexType}`
                  }
                ]
              },
              valueQuantity: {
                value: parseInt(value),
                unit: 'Db',
                system: 'http://unitsofmeasure.org',
                code: 'Db'
              }
            });
          }
        });
      });
    
      // Diskriminationsverlust
      if (ohrData.diskriminationsverlust) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99912-1',
                display: `Diskriminationsverlust ${side}`
              }
            ]
          },
          valueQuantity: {
            value: parseInt(ohrData.diskriminationsverlust),
            unit: '%',
            system: 'http://unitsofmeasure.org',
            code: '%'
          }
        });
      }
    
      // Gehörgang
      if (ohrData.gehoergang.erweitert || ohrData.gehoergang.eng) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99913-1',
                display: `Gehörgang ${side}`
              }
            ]
          },
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://loinc.org',
                code: ohrData.gehoergang.erweitert ? '99914-1' : '99915-1',
                display: ohrData.gehoergang.erweitert ? 'Erweitert' : 'Eng'
              }
            ]
          }
        });
      }
    
      // Trommelfell
      if (ohrData.trommelfell.durchloechert || ohrData.trommelfell.feucht) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99916-1',
                display: `Trommelfell ${side}`
              }
            ]
          },
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://loinc.org',
                code: ohrData.trommelfell.durchloechert ? '99917-1' : '99918-1',
                display: ohrData.trommelfell.durchloechert ? 'Durchlöchert' : 'Feucht'
              }
            ]
          }
        });
      }
    
      // Hörhilfe notwendig
      if (ohrData.hoerhilfeNotwendig) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99919-1',
                display: `Hörhilfe notwendig ${side}`
              }
            ]
          },
          valueBoolean: ohrData.hoerhilfeNotwendig
        });
      }
    
      // Hörweite
      if (ohrData.hoorweite.FlSpr || ohrData.hoorweite.UmSpr) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99920-1',
                display: `Hörweite ${side}`
              }
            ]
          },
          valueString: `FlSpr: ${ohrData.hoorweite.FlSpr}, UmSpr: ${ohrData.hoorweite.UmSpr}`
        });
      }
    
      // MO-Druck H2O
      if (ohrData.moDruckH2O) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99921-1',
                display: `MO-Druck H2O ${side}`
              }
            ]
          },
          valueQuantity: {
            value: parseInt(ohrData.moDruckH2O),
            unit: 'H2O',
            system: 'http://unitsofmeasure.org',
            code: 'H2O'
          }
        });
      }
    
      // Testgeräusch Lautstärke
      if (ohrData.testgeraeuschLautstaerke) {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99922-1',
                display: `Testgeräusch Lautstärke ${side}`
              }
            ]
          },
          valueQuantity: {
            value: parseInt(ohrData.testgeraeuschLautstaerke),
            unit: 'Db',
            system: 'http://unitsofmeasure.org',
            code: 'Db'
          }
        });
      }
    
      // Tinnitushilfe
      if (ohrData.tinnitushilfe.notwendig) {
        parameters.push(
          {
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: '99922-1',
                  display: `Tinnitushilfe Frequenz ${side}`
                }
              ]
            },
            valueQuantity: {
              value: parseFloat(ohrData.tinnitushilfe.frequenz),
              unit: 'KHz',
              system: 'http://unitsofmeasure.org',
              code: 'KHz'
            }
          },
          {
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: '99923-1',
                  display: `Tinnitushilfe verdeckbar Lautstärke ${side}`
                }
              ]
            },
            valueQuantity: {
              value: parseInt(ohrData.tinnitushilfe.verdeckbarLautstaerke),
              unit: 'Db',
              system: 'http://unitsofmeasure.org',
              code: 'Db'
            }
          },
          {
            code: {
              coding: [
                {
                  system: 'http://loinc.org',
                  code: '99924-1',
                  display: `Tinnitushilfe notwendig ${side}`
                }
              ]
            },
            valueBoolean: ohrData.tinnitushilfe.notwendig
          }
        );
      }
    
      // Weber-Test Ergebnis
      if (side === 'Links') {
        parameters.push({
          code: {
            coding: [
              {
                system: 'http://loinc.org',
                code: '99702-0',
                display: 'Weber-Test Ergebnis: Links'
              }
            ]
          },
          valueBoolean: ohrData.weberTestErgebnis
        });
      }
    
      return parameters;
    },
    
    // Hilfsfunktion zum Abrufen des LOINC-Codes für Hörverlust
    getLoincCodeForHoerverlust(freq, side) {
      const loincCodes = {
        '10KHz': side === 'Rechts' ? '99300-2' : '99301-0',
        '8KHz': side === 'Rechts' ? '89029-3' : '89030-1',
        '6KHz': side === 'Rechts' ? '89027-7' : '89028-5',
        '4KHz': side === 'Rechts' ? '89023-6' : '89024-4',
        '3KHz': side === 'Rechts' ? '89021-0' : '89022-8',
        '2KHz': side === 'Rechts' ? '89019-4' : '89020-2',
        '15KHz': side === 'Rechts' ? '99302-8' : '99303-6',
        '1KHz': side === 'Rechts' ? '89017-8' : '89018-6',
        '05KHz': side === 'Rechts' ? '89015-2' : '89016-0',
        '025KHz': side === 'Rechts' ? '89013-7' : '89014-5',
        '0125KHz': side === 'Rechts' ? '99304-4' : '99305-1'
      };
    
      return loincCodes[freq];
    }
    
  }
  
});